// lib/overlayHistory.ts
// Every open overlay owns one history entry, so the browser Back gesture closes the top
// overlay instead of leaving the app (the SPA otherwise pushes no entry at all).

type OverlayEntry = { close: () => void };

const stack: OverlayEntry[] = [];
// Hrefs to restore once the pop caused by an in-app close lands: a URL written while the
// overlay was open (a session switch) must survive going back to the entry beneath it.
const pendingInAppPops: string[] = [];
let deferredPushes = 0;
let listening = false;

const pushEntry = () => window.history.pushState({ overlayDepth: stack.length }, '', window.location.href);

function handlePopState(event: PopStateEvent) {
  const restoreHref = pendingInAppPops.shift();
  if (restoreHref !== undefined) {
    event.stopImmediatePropagation();
    if (window.location.href !== restoreHref) window.history.replaceState(window.history.state, '', restoreHref);
    // history.back() is asynchronous, so an overlay opened meanwhile waits for it to land;
    // pushing first would have been the entry that back() removed.
    for (; deferredPushes > 0; deferredPushes -= 1) pushEntry();
    return;
  }
  const entry = stack.pop();
  if (!entry) return;
  event.stopImmediatePropagation();
  entry.close();
}

/** Registers an open overlay; the returned release is called when it closes in-app. */
export function pushOverlayHistory(close: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  if (!listening) {
    // Capture runs before the URL synchronizer's listener, which must not see a consumed pop.
    window.addEventListener('popstate', handlePopState, true);
    listening = true;
  }
  const entry: OverlayEntry = { close };
  stack.push(entry);
  if (pendingInAppPops.length > 0) deferredPushes += 1;
  else pushEntry();
  return () => {
    const index = stack.indexOf(entry);
    if (index === -1) return;
    stack.splice(index, 1);
    if (deferredPushes > 0) {
      deferredPushes -= 1;
      return;
    }
    pendingInAppPops.push(window.location.href);
    window.history.back();
  };
}
